/**
 * The ADR 060 `Rate` value type: an exact, dimensionless fraction at the
 * `numeric(12,6)` storage scale. Not money, and it carries no currency.
 */
import Decimal from 'decimal.js';

import { decodeRateFraction, encodeRateFraction, type RateWire } from './codec';

/** Fraction digits every rate column holds (ADR 060 — Money Type § 2.5). */
export const RATE_SCALE = 6;

/** Decimals a percent can carry and still fit a Rate exactly: six fraction places are four after the percent point. */
export const RATE_PERCENT_DECIMALS = RATE_SCALE - 2;

/**
 * The inline message on a percent field whose value is finer than a Rate holds
 * (BUG-2051, BL-2455, BUG-2076): one wording for every percent the house types,
 * so the buyer's-premium, late-fee, tax-rate and seller-commission rates refuse alike.
 */
export const RATE_PRECISION_MESSAGE = `Use at most ${RATE_PERCENT_DECIMALS} decimal places.`;

/** What a `Rate` constructor accepts — see {@link Rate.fromPercent} for why a number is safe here. */
export type RateInput = Decimal | string | number;

/**
 * A percentage, held as its exact fraction (8.375% is `0.083750`). Built only
 * by naming which form you have — {@link Rate.fromPercent} or
 * {@link Rate.fromFraction} — so the ×100 / ÷100 confusion cannot recur, and
 * applied to money only through `money.times(rate, mode)`. Immutable.
 */
export class Rate {
    private constructor(private readonly fraction: Decimal) {}

    /**
     * Builds a rate from a percentage, as an operator types it (`8.375` for 8.375%).
     *
     * A JS `number` is accepted because a form field hands one over: decimal.js
     * parses it through its shortest round-trip string, so `8.2` is exactly `8.2`
     * — the division happens in Decimal, never as `8.2 / 100` on a float.
     *
     * @param percent - The percentage.
     * @returns The rate, normalized half-up to the six-place storage scale.
     * @throws TypeError when the value is not finite.
     */
    static fromPercent(percent: RateInput): Rate {
        return Rate.normalize(toFiniteDecimal(percent, 'fromPercent').dividedBy(100));
    }

    /**
     * Whether a percentage fits a `Rate` exactly, so {@link Rate.fromPercent}
     * would not round it — the question a percent form field asks before saving
     * (BUG-2051). `16.6667` fits; `16.66666` does not.
     *
     * @param percent - The percentage, as an operator types it.
     * @returns True when the value is finite and has at most {@link RATE_PERCENT_DECIMALS} decimals.
     */
    static holdsPercent(percent: RateInput): boolean {
        const decimal = new Decimal(percent);
        return decimal.isFinite() && decimal.decimalPlaces() <= RATE_PERCENT_DECIMALS;
    }

    /**
     * Whether a fraction fits a `Rate` exactly, so {@link Rate.fromFraction}
     * would not round it — the server's half of {@link Rate.holdsPercent}, for a
     * write body that carries fractions (the seller-commission schedule,
     * BUG-2076). `0.166667` fits; `0.1666667` does not.
     *
     * @param fraction - The fraction, as the write body carries it.
     * @returns True when the value is finite and has at most {@link RATE_SCALE} decimals.
     */
    static holdsFraction(fraction: RateInput): boolean {
        const decimal = new Decimal(fraction);
        return decimal.isFinite() && decimal.decimalPlaces() <= RATE_SCALE;
    }

    /**
     * Builds a rate from its fraction (`0.083750` for 8.375%) — what the
     * `rateNumeric` column decodes to.
     *
     * @param fraction - The fraction.
     * @returns The rate, normalized half-up to the six-place storage scale.
     * @throws TypeError when the value is not finite.
     */
    static fromFraction(fraction: RateInput): Rate {
        return Rate.normalize(toFiniteDecimal(fraction, 'fromFraction'));
    }

    /**
     * Decodes a rate off the wire (BL-2091) — the inverse of {@link Rate.toWire}.
     *
     * @param wire - The `RateWire` a DTO carries.
     * @returns The rate.
     * @throws TypeError when the wire fraction is not a plain decimal string.
     */
    static fromWire(wire: RateWire): Rate {
        return Rate.fromFraction(decodeRateFraction(wire));
    }

    /** @returns The wire form — the exact fraction at six places (`{ fraction: "0.083750" }`). */
    toWire(): RateWire {
        return encodeRateFraction(this.fraction);
    }

    /**
     * Adds two rates exactly — how a tax rate's three components become its total,
     * without ever passing through a JS number.
     *
     * @param other - The rate to add.
     * @returns The sum, at the storage scale.
     */
    plus(other: Rate): Rate {
        return Rate.normalize(this.fraction.plus(other.fraction));
    }

    /** @returns The exact fraction — for a repository write or `Money.times`. */
    toFraction(): Decimal {
        return this.fraction;
    }

    /** @returns The exact percentage (`8.375`), for an editor's input. */
    toPercentDecimal(): Decimal {
        return this.fraction.times(100);
    }

    /** @returns The display string, trailing zeros trimmed: `"8.375%"`, `"8.5%"`, `"0%"`. */
    toPercent(): string {
        return `${this.toPercentDecimal().toString()}%`;
    }

    /**
     * @param other - Another rate.
     * @returns True when both hold the same fraction.
     */
    equals(other: Rate): boolean {
        return this.fraction.equals(other.fraction);
    }

    /** @returns True when the rate is exactly zero. */
    isZero(): boolean {
        return this.fraction.isZero();
    }

    /** @returns The display string — so an accidental `${rate}` renders a percent. */
    toString(): string {
        return this.toPercent();
    }

    private static normalize(value: Decimal): Rate {
        return new Rate(value.toDecimalPlaces(RATE_SCALE, Decimal.ROUND_HALF_UP));
    }
}

function toFiniteDecimal(value: RateInput, op: string): Decimal {
    const decimal = new Decimal(value);
    if (!decimal.isFinite()) {
        throw new TypeError(`Rate.${op}: value must be finite, got ${decimal.toString()}`);
    }
    return decimal;
}
